import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Dispatch, SetStateAction } from 'react';
import { useToast } from '../../../../../../providers/ToastProvider';
import { StockLocationInput } from '../../../../../../types/stock_location/stockLocation.input';
import { countries } from '../../../../../../util/country';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import { TextInput } from '../../../../../components/TextInput';
import * as service from 'azure-maps-rest';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { Coordinates } from '../../../../../../types/common/address';
import { ToastType } from '../../../../../components/Toast';
import { AddressUtil } from '../../../../../../types/common/address.util';
import { t } from '../../../../../../types/translation/Translator';
import ClickableMap from '../../../../../components/Map/ClickableMap';
import { CompanyRoleAssignmentContext } from '../../../../../../context/CompanyRoleAssignmentContext';
import { TagRelationAuthorization } from '../../../../../../types/tag';
import { IconButton } from '../../../../../icons';
import { IconType } from '../../../../../icons/Icon';
import { MobileStockLocationSession } from '../../../../../../types/mobile_stock_location';
import { useSubscription } from '@apollo/client';
import {
  GetMobileStockLocationSessionsResponse,
  GetMobileStockLocationSessionsVariables,
  MobileStockLocationQueries,
  MobileStockLocationSessionNotificationResponse,
  MobileStockLocationSessionNotificationVariables,
  MobileStockLocationSubscriptions,
} from '../../../../../../graphql/mobileStockLocation.graphql';
import LoadingPackage from '../../../../Common/LoadingPackage';
import useQuery from '../../../../../../hooks/useQuery';
import StockLocationMap from '../../../../../components/Map/StockLocationMap';
import { UserContext } from '../../../../../../context/UserContext';
import { StockLocationMarker, StockLocationMarkerLayer } from '../../../../../components/Map';
import MobileStockLocationSessionProvider from '../../../../../../providers/MobileStockLocationSessionProvider';
import { Checkbox } from '../../../../../components/Checkbox';

interface StockLocationInputPaneProps {
  input: StockLocationInput;
  setInput: Dispatch<SetStateAction<StockLocationInput>>;
  tagInput: React.ReactNode;
  footer: React.ReactNode;
}

export default function StockLocationInputPane({ input, setInput, tagInput, footer }: StockLocationInputPaneProps) {
  const { showToast } = useToast();

  const { hasRole } = useContext(CompanyRoleAssignmentContext);
  const { companyUsers } = useContext(UserContext);

  const [session, setSession] = useState<MobileStockLocationSession | null>(null);

  const [coordinatesLoading, setCoordinatesLoading] = useState(false);
  const [coordinates, _setCoordinates] = useState(input.address?.coordinates);
  const setCoordinates = (coordinates: Coordinates | undefined) => {
    _setCoordinates(coordinates);
    setInput(input => input.withCoordinates(coordinates));
  };

  const { loading } = useQuery<GetMobileStockLocationSessionsResponse, GetMobileStockLocationSessionsVariables>(
    MobileStockLocationQueries.get,
    {
      variables: { page: 0 },
      onCompleted: response => {
        handleSessions(response.mobileStockLocationSessions.data);
      },
    },
  );

  const { data } = useSubscription<
    MobileStockLocationSessionNotificationResponse,
    MobileStockLocationSessionNotificationVariables
  >(MobileStockLocationSubscriptions.sessions, {
    variables: { companyId: input.companyId, stockLocationIds: [input.id] },
  });

  useEffect(() => {
    if (data && data.mobileStockLocationSessionNotification) {
      handleSessions(data.mobileStockLocationSessionNotification.entities);
    }
  }, [data]);

  const handleSessions = (sessions: MobileStockLocationSession[]) => {
    const sessionForCurrentLocation = sessions.find(s => s.stockLocationId === input.id);
    if (sessionForCurrentLocation) {
      setSession(new MobileStockLocationSession(sessionForCurrentLocation));
    }
  };

  const synchronizeCoordinates = async () => {
    const hasInvalidAddress =
      !input.address?.addressLine1 &&
      !input.address?.addressLine2 &&
      !input.address?.city &&
      !input.address?.countryCode &&
      !input.address?.region;

    if (hasInvalidAddress) {
      showToast({
        type: ToastType.error,
        title: t(TranslationKey.insufficientAddressInformationToAttemptCoordinatesLookupToastTitle).singular.upper,
      });
      return setCoordinates(undefined);
    }

    setCoordinatesLoading(true);
    const key = process.env.REACT_APP_AZURE_MAPS_SUBSCRIPTION_KEY || 'no_key';
    const pipeline = service.MapsURL.newPipeline(new service.SubscriptionKeyCredential(key));
    await new service.SearchURL(pipeline)
      .searchAddress(service.Aborter.timeout(3000), AddressUtil.addressToSearchQuery(input.address))
      .then(results => {
        if (!results.results?.length) {
          showToast({
            type: ToastType.error,
            title: t(TranslationKey.unableToFindValidCoordinatesForThisAddressToastTitle).singular.upper,
          });
          return setCoordinates(undefined);
        }

        const { position } = results.results[0];
        if (position && position.lat && position.lon) {
          setCoordinates({
            latitude: position.lat,
            longitude: position.lon,
          });
        }
      });
    setCoordinatesLoading(false);
  };

  const map = useMemo(() => {
    if (session) {
      const stockLocationIds = new Set([input.id]);
      return (
        <MobileStockLocationSessionProvider stockLocationIds={stockLocationIds}>
          <StockLocationMap<StockLocationMarker>
            markerLayer={new StockLocationMarkerLayer()}
            stockLocationIds={stockLocationIds}
          />
        </MobileStockLocationSessionProvider>
      );
    } else {
      return (
        <ClickableMap
          stockLocation={input}
          zoom={12}
          coordinates={coordinates}
          onClick={coordinates => setCoordinates(coordinates)}
        />
      );
    }
  }, [coordinates, session]);

  if (loading) {
    return (
      <div className='flex flex-grow items-center justify-center'>
        <LoadingPackage />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8 overflow-hidden'>
      <div className='grid flex-grow grid-cols-2 gap-8 overflow-auto pr-2'>
        <div className='grid h-fit gap-4'>
          <TextInput
            mandatory
            value={input.name}
            label={t(TranslationKey.stockLocationName).singular.upper}
            onChange={value => setInput(input.withName(value))}
            placeholder={t(TranslationKey.stockLocationNameInputPlaceholder).singular.upper} //'Ex: Warehouse B Antwerpen'
          />
          <TextInput
            label={t(TranslationKey.stockLocationIdentifier).singular.upper}
            value={input.identifier}
            onChange={value => setInput(input.withIdentifier(value))}
            placeholder={t(TranslationKey.stockLocationIdentifierInputPlaceholder).singular.upper} // 'Ex: WH AB'
          />
          <Checkbox
            value={input.mobile || false}
            onChange={value => setInput(input.withMobile(value))}
            label={t(TranslationKey.stockLocationMobileInputLabel).singular.upper}
            tooltip={t(TranslationKey.stockLocationMobileInputTooltip).singular.upper}
          />
          <TextInput
            label={t(TranslationKey.addressLine1).singular.upper}
            value={input.address?.addressLine1}
            onChange={value => setInput(input.withAddressLine1(value))}
            placeholder={t(TranslationKey.addressLine1InputPlaceholder).singular.upper}
          />
          <TextInput
            label={t(TranslationKey.addressLine2).singular.upper}
            value={input.address?.addressLine2}
            onChange={value => setInput(input.withAddressLine2(value))}
            placeholder={t(TranslationKey.addressLine2InputPlaceholder).singular.upper}
          />
          <div className={'grid grid-cols-2 gap-4'}>
            <TextInput
              value={input.address?.postalCode}
              label={t(TranslationKey.postalCode).singular.upper}
              placeholder={t(TranslationKey.postalCodeInputPlaceholder).singular.upper}
              onChange={value => setInput(input.withPostalCode(value))}
            />
            <TextInput
              placeholder={t(TranslationKey.cityInputPlaceholder).singular.upper}
              label={t(TranslationKey.city).singular.upper}
              onChange={value => setInput(input.withCity(value))}
              value={input.address?.city}
            />
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <TextInput
              placeholder={t(TranslationKey.regionInputPlaceholder).singular.upper}
              label={t(TranslationKey.region).singular.upper}
              onChange={value => setInput(input.withRegion(value))}
              value={input.address?.region}
            />
            <DropdownSearchSelect
              label={t(TranslationKey.country).singular.upper}
              placeholder={t(TranslationKey.countryInputPlaceholder).singular.upper}
              items={countries.map(c => c.cca2)}
              onSelect={value => setInput(input => input.withCountryCode(value))}
              value={input.address?.countryCode}
              textBuilder={value =>
                countries.find(c => c.cca2 === value)?.name.common || t(TranslationKey.unknownCountry).singular.upper
              }
            />
          </div>
          {hasRole(new TagRelationAuthorization().forCreate()) && tagInput}
        </div>
        <div className='relative h-fit'>
          <div className='h-[500px]'>{map}</div>
          {!session ? (
            <div className='absolute bottom-2 left-2 w-fit'>
              <IconButton
                type={IconType.refreshCw5}
                onClick={() => synchronizeCoordinates()}
                text={t(TranslationKey.syncAddress).singular.upper}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div>{footer}</div>
    </div>
  );
}
